// Project row (collapsed-by-default header + expandable subtask table).
// One row per parent task. The Avatar + Prio + StatusPill imports are
// shared atoms; isOverdue/overdueDays/fmtShort live in lib/dates.

import { useMemo, useState } from 'react';
import { Avatar } from './Avatar.jsx';
import { Prio, StatusPill } from './atoms.jsx';
import { isOverdue, overdueDays, fmtShort } from '../lib/dates.ts';
import { SubtaskTree } from './SubtaskTree.jsx';

// All descendant tasks of `rootId` (any depth) via the parentId graph.
// Used by the project header to count "涉及人 / 完成进度" across nested splits.
function collectDescendants(rootId, allTasks) {
  const out = [];
  const stack = [rootId];
  while (stack.length) {
    const id = stack.pop();
    for (const t of allTasks) {
      if (t.parentId === id) {
        out.push(t);
        stack.push(t.id);
      }
    }
  }
  return out;
}

export const ProjectRow = ({
  project, subtasks, staff, onOpen, defaultOpen,
  // Drag-and-drop props (parent owns the order state + persistence).
  // Optional — if omitted, the row renders without drag affordance.
  draggable, onDragStart, onDragOver, onDragLeave, onDrop, dragState,
  // allTasks lets the row build a recursive tree of nested splits
  // (Regina → 待办 → 店长 → store splits). Falls back to direct
  // `subtasks` for legacy callers that haven't been threaded through yet.
  allTasks,
  // Optional edit handlers — when provided, the subtask peek panel becomes editable.
  updateTask, addUpdate
}) => {
  const [open, setOpen] = useState(!!defaultOpen);
  // Stats include every descendant, not just direct children, so the
  // header progress bar reflects the chain-split case where 店长 broke
  // a 待办 into sub-splits of their own.
  const descendants = useMemo(
    () => allTasks ? collectDescendants(project.id, allTasks) : subtasks,
    [project.id, allTasks, subtasks]
  );
  const done = descendants.filter(t => t.status === '已完成').length;
  const total = descendants.length;
  const blocked = descendants.filter(t => t.status === '阻塞').length;
  const overdue = descendants.filter(isOverdue).length;
  const pct = total ? Math.round(done / total * 100) : 0;
  const owner = staff.find(s => s.id === project.assigneeId);
  const projOverdue = isOverdue(project);

  // dragState: 'dragging' | 'over' | null — drives the visual feedback.
  const dragClass = dragState === 'dragging' ? ' is-dragging'
                  : dragState === 'over'     ? ' is-drag-over'
                  : '';

  return (
    <section className={`project-row${dragClass}`}
             draggable={!!draggable}
             onDragStart={(e) => onDragStart?.(e, project.id)}
             onDragOver={(e)  => onDragOver?.(e, project.id)}
             onDragLeave={(e) => onDragLeave?.(e, project.id)}
             onDrop={(e)      => onDrop?.(e, project.id)}>
      <header className="project-header" onClick={() => setOpen(!open)}
              role="button" aria-expanded={open} tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setOpen(!open))}>
        {draggable && (
          <div className="project-drag-handle" aria-hidden
               title="拖动排序"
               onClick={(e) => e.stopPropagation()}>⋮⋮</div>
        )}
        <div className="project-chevron" aria-hidden>{open ? '▾' : '▸'}</div>
        <div className="project-main">
          <div className="project-title-row">
            <Prio value={project.priority}/>
            <h3 className="project-title">{project.title}</h3>
            {projOverdue && <span className="project-overdue-badge">逾期 {overdueDays(project)} 天</span>}
          </div>
          <div className="project-meta">
            <span>主负责人 <Avatar staff={owner}/> <strong>{owner?.name}</strong></span>
            <span className="mono">截止 {fmtShort(project.dueDate)}</span>
            <span>· 涉及 <strong>{new Set(descendants.map(t => t.assigneeId).filter(Boolean)).size}</strong> 人</span>
            {blocked > 0 && <span style={{ color: 'var(--vava-red)' }}>· {blocked} 阻塞</span>}
            {overdue > 0 && <span style={{ color: 'var(--vava-red)' }}>· {overdue} 逾期</span>}
          </div>
        </div>
        <div className="project-progress">
          <div className="project-progress-text mono">{done}/{total}</div>
          <div className="progress-bar" style={{ width: 120 }}><span className="fill" style={{ width: pct + '%' }}/></div>
          <div className="project-progress-pct mono">{pct}%</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); onOpen(project.id); }}>
          详情
        </button>
      </header>
      {open && (
        <div className="project-subtasks">
          {allTasks
            ? <SubtaskTree parent={project} allTasks={allTasks} staff={staff} onOpen={onOpen}
                           updateTask={updateTask} addUpdate={addUpdate} />
            : (
              // Legacy fallback for callers that don't pass allTasks: render
              // a flat table of direct subtasks. Same shape as before so
              // nothing breaks if a route hasn't been threaded through yet.
              <table className="vava-table" style={{ background: 'transparent' }}>
                <thead>
                  <tr>
                    <th style={{ width: 180 }}>负责人</th>
                    <th>子项</th>
                    <th style={{ width: 80 }}>优先级</th>
                    <th style={{ width: 100 }}>截止</th>
                    <th style={{ width: 100 }}>状态</th>
                  </tr>
                </thead>
                <tbody>
                  {subtasks.map(t => {
                    const who = staff.find(s => s.id === t.assigneeId);
                    const over = isOverdue(t);
                    return (
                      <tr key={t.id} onClick={() => onOpen(t.id)}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Avatar staff={who}/>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>{who?.name}</div>
                              <div style={{ fontSize: 11, color: 'var(--vava-text-muted)' }}>{who?.title}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ fontWeight: 500 }}>{t.title}</td>
                        <td><Prio value={t.priority}/></td>
                        <td className="mono" style={{ color: over ? 'var(--vava-red)' : 'inherit', fontWeight: over ? 600 : 400 }}>
                          {fmtShort(t.dueDate)}{over && ' · 逾期'}
                        </td>
                        <td><StatusPill value={t.status}/></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          }
        </div>
      )}
    </section>
  );
};
